import React, { createContext, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Loading from "../components/Loading";
import { clearCookie, getCookie, setCookie } from "../utilities/cookies";
import { buildAuthenticatedDestination, rememberRedirectUrl, resolveRedirectUrl } from "../utilities/authRedirect";
import { beginIdentityTransaction, getIdentityTransaction, updateIdentityTransaction } from "../gateway/identityTransaction";
import { getRealtimeSessionId } from "../utilities/realtimeSession";

const authorizeContext = createContext();
const INTERNAL_PROTECTED_ROUTES = new Set(["/security-center", "/security-enrollment"]);
const AUTO_LOGIN_EXCLUDED_ROUTES = new Set(["/logout", "/security-verification", ...INTERNAL_PROTECTED_ROUTES]);


const hasUsableSession = (session) => Boolean(
  session?.success && session?.user?._id && (session?.token || session?.sessionToken)
);

const AuthorizationProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [content, setContent] = useState(children);
  const [user, setUser] = useState({});
  const [redirectUrl, setRedirectUrl] = useState("");
  const [referrer, setReferrer] = useState("");

  useEffect(() => {
    beginIdentityTransaction({ search: location.search, pathname: location.pathname });
    if (INTERNAL_PROTECTED_ROUTES.has(location.pathname)) {
      updateIdentityTransaction({ requestType: "internal", internalReturnPath: location.pathname, clientId: "accounts", protocolVersion: "2", redirectUri: "" });
    }
  }, [location.pathname, location.search]);

  const refreshSession = async (session) => {
    const formData = new FormData();
    formData.append("sessionToken", session.sessionToken);
    formData.append("userId", session.user._id);
    const realtimeSessionId = getRealtimeSessionId();
    if (realtimeSessionId) formData.append("realtime_session_id", realtimeSessionId);
    const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/auth/session/login`, {
      method: "POST",
      body: formData,
    });
    const payload = await response.json();
    if (!response.ok || !payload.success) {
      throw new Error(payload.message || payload.error || "Your session has expired");
    }
    setCookie("user", payload);
    setUser(payload.user || {});
    return payload;
  };

  useEffect(() => {
    let active = true;
    const initialize = async () => {
      const queryParameters = new URLSearchParams(window.location.search);
      const transaction = getIdentityTransaction() || beginIdentityTransaction({ search: location.search, pathname: location.pathname });
      const queryRedirect = transaction?.redirectUri || queryParameters.get("redirectto");
      const redirectTo = rememberRedirectUrl(queryRedirect) || resolveRedirectUrl();
      setRedirectUrl(redirectTo);
      setReferrer(queryParameters.get("referrer") || document.referrer || "");

      const session = getCookie("user");
      const isInternalProtectedRoute = INTERNAL_PROTECTED_ROUTES.has(location.pathname);

      if (isInternalProtectedRoute) {
        if (!hasUsableSession(session)) {
          updateIdentityTransaction({ requestType: "internal", internalReturnPath: location.pathname, clientId: "accounts", protocolVersion: "2", redirectUri: "" });
          if (active) navigate("/login", { replace: true });
          return;
        }

        if (!session.sessionToken && session.token) {
          setUser(session.user || {});
          setContent(children);
          return;
        }

        try {
          setContent(<Loading message="Please wait, restoring your secure session..." />);
          await refreshSession(session);
          if (active) setContent(children);
        } catch (error) {
          clearCookie("user");
          updateIdentityTransaction({ requestType: "internal", internalReturnPath: location.pathname, clientId: "accounts", protocolVersion: "2", redirectUri: "" });
          if (active) navigate("/login", { replace: true });
        }
        return;
      }

      if (AUTO_LOGIN_EXCLUDED_ROUTES.has(location.pathname) || !hasUsableSession(session)) {
        setContent(children);
        return;
      }

      try {
        setContent(<Loading message="Please wait, logging you in..." />);
        const payload = session.token ? session : await refreshSession(session);
        if (!redirectTo) {
          window.location.replace(buildAuthenticatedDestination({ userId: payload.user?._id, refreshToken: payload.refreshToken }));
          return;
        }
        const destination = buildAuthenticatedDestination({
          redirectUrl: redirectTo,
          userId: payload.user?._id,
          refreshToken: payload.refreshToken,
        });
        window.location.replace(destination);
      } catch (error) {
        clearCookie("user");
        if (active) setContent(children);
      }
    };

    initialize();
    return () => { active = false; };
  }, [children, location.pathname, location.search, navigate]);

  return (
    <authorizeContext.Provider value={{ redirectUrl, setRedirectUrl, user, setUser, referrer }}>
      {content}
    </authorizeContext.Provider>
  );
};

const useRedirectUrl = () => useContext(authorizeContext).redirectUrl;
const useUser = () => useContext(authorizeContext)?.user;
const useSetUser = () => useContext(authorizeContext)?.setUser;
const useReferrer = () => useContext(authorizeContext)?.referrer;

export default AuthorizationProvider;
export { useRedirectUrl, useUser, useSetUser, useReferrer };
